import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { guardJeridSchoolAdminOnly } from '../../lib/auth/routeGuards'
import { jeridSchoolAdminService } from '@/lib/api/services/jerid-school-admin-service'
import type {
  CreateEstablishmentDTO,
  Establishment,
  SuperAdmin,
  UpdateEstablishmentDTO,
} from '@/interface/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/components/ui/use-toast'
import {
  AlertTriangle,
  Building2,
  Edit,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react'

export const Route = createFileRoute('/jerid_school_admin/establishments')({
  beforeLoad: guardJeridSchoolAdminOnly,
  component: RouteComponent,
})

const emptyForm: CreateEstablishmentDTO = {
  name: '',
  address: '',
  url: '',
  logo: '',
  description: '',
  contactEmail: '',
  contactPhone: '',
  isActive: true,
  superAdminId: '',
}

function RouteComponent() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Establishment | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Establishment | null>(null)

  const establishmentsQuery = useQuery({
    queryKey: ['jerid-admin-establishments'],
    queryFn: jeridSchoolAdminService.getEstablishments,
  })

  const directorsQuery = useQuery({
    queryKey: ['jerid-admin-school-directors'],
    queryFn: jeridSchoolAdminService.getSchoolDirectors,
  })

  const invalidateData = () => {
    queryClient.invalidateQueries({ queryKey: ['jerid-admin-establishments'] })
    queryClient.invalidateQueries({ queryKey: ['jerid-school-admin-dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['jerid-admin-school-directors'] })
  }

  const createMutation = useMutation({
    mutationFn: jeridSchoolAdminService.createEstablishment,
    onSuccess: () => {
      invalidateData()
      setIsCreateOpen(false)
      toast({ title: 'Establishment created' })
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create establishment',
        description: error?.response?.data?.message || 'Please check the form.',
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: UpdateEstablishmentDTO
    }) => jeridSchoolAdminService.updateEstablishment(id, data),
    onSuccess: () => {
      invalidateData()
      setEditing(null)
      toast({ title: 'Establishment updated' })
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update establishment',
        description: error?.response?.data?.message || 'Please check the form.',
        variant: 'destructive',
      })
    },
  })

  const statusMutation = useMutation({
    mutationFn: (school: Establishment) =>
      school.isActive
        ? jeridSchoolAdminService.deactivateEstablishment(String(school.id))
        : jeridSchoolAdminService.activateEstablishment(String(school.id)),
    onSuccess: () => {
      invalidateData()
      toast({ title: 'Establishment status updated' })
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update status',
        description: error?.response?.data?.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: jeridSchoolAdminService.deleteEstablishment,
    onSuccess: () => {
      invalidateData()
      setDeleteTarget(null)
      toast({ title: 'Establishment deleted' })
    },
    onError: (error: any) => {
      toast({
        title: 'Could not delete establishment',
        description:
          error?.response?.data?.message ||
          'Deactivate schools that already have records.',
        variant: 'destructive',
      })
    },
  })

  const establishments = establishmentsQuery.data ?? []
  const directors = directorsQuery.data ?? []

  const filtered = useMemo(() => {
    return establishments.filter((school) => {
      const matchesStatus =
        status === 'all' ||
        (status === 'active' ? school.isActive : !school.isActive)
      const haystack = `${school.name} ${school.address} ${school.url ?? ''} ${
        school.superAdmin?.firstname ?? ''
      } ${school.superAdmin?.lastname ?? ''}`.toLowerCase()

      return matchesStatus && haystack.includes(search.toLowerCase())
    })
  }, [establishments, search, status])

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal text-slate-950">
            Establishments
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Create schools, assign directors, and manage platform status.
          </p>
        </div>
        <Button className="w-fit gap-2" onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Add School
        </Button>
      </div>

      <Card className="rounded-md border-slate-200">
        <CardContent className="flex flex-col gap-3 p-4 lg:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search schools, locations, or directors"
              className="pl-9"
            />
          </div>
          <Select value={status} onValueChange={(value: any) => setStatus(value)}>
            <SelectTrigger className="w-full lg:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => establishmentsQuery.refetch()}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </CardContent>
      </Card>

      {establishmentsQuery.isLoading && (
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="h-48 animate-pulse rounded-md bg-white" />
          ))}
        </div>
      )}

      {establishmentsQuery.isError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="flex items-start gap-3 p-5 text-red-800">
            <AlertTriangle className="mt-0.5 h-5 w-5" />
            <div>
              <p className="font-medium">Establishments could not be loaded</p>
              <p className="mt-1 text-sm">
                The page is using the Jerid admin endpoint. Retry after checking
                the API response.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!establishmentsQuery.isLoading && !establishmentsQuery.isError && (
        <>
          {filtered.length === 0 ? (
            <Card className="rounded-md border-dashed">
              <CardContent className="py-12 text-center">
                <Building2 className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 font-medium text-slate-700">
                  No establishments found
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  Create a school or adjust the current filters.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {filtered.map((school) => (
                <Card key={school.id} className="rounded-md border-slate-200">
                  <CardContent className="space-y-4 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="font-semibold text-slate-950">
                          {school.name}
                        </h2>
                        <div className="mt-1 flex items-start gap-1.5 text-sm text-slate-500">
                          <MapPin className="mt-0.5 h-3.5 w-3.5" />
                          <span>{school.address}</span>
                        </div>
                      </div>
                      <Badge variant={school.isActive ? 'default' : 'secondary'}>
                        {school.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    <div className="rounded-md bg-slate-50 p-3 text-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                        Director
                      </p>
                      <p className="mt-1 font-medium text-slate-800">
                        {school.superAdmin
                          ? `${school.superAdmin.firstname} ${school.superAdmin.lastname}`
                          : 'Unassigned'}
                      </p>
                      {school.superAdmin?.email && (
                        <p className="text-slate-500">{school.superAdmin.email}</p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => setEditing(school)}
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => statusMutation.mutate(school)}
                        disabled={statusMutation.isPending}
                      >
                        {school.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 text-red-600 hover:text-red-700"
                        onClick={() => setDeleteTarget(school)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <EstablishmentDialog
        title="Create establishment"
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        directors={directors}
        initialValue={emptyForm}
        isSubmitting={createMutation.isPending}
        onSubmit={(data) => createMutation.mutate(data)}
      />

      {editing && (
        <EstablishmentDialog
          title="Edit establishment"
          open={!!editing}
          onOpenChange={(open) => !open && setEditing(null)}
          directors={directors}
          initialValue={{
            name: editing.name,
            address: editing.address,
            url: editing.url ?? '',
            logo: editing.logo ?? '',
            description: editing.description ?? '',
            contactEmail: editing.contactEmail ?? '',
            contactPhone: editing.contactPhone ?? '',
            isActive: editing.isActive,
            superAdminId: String(
              editing.superAdminId ?? editing.superAdmin?.id ?? '',
            ),
          }}
          isSubmitting={updateMutation.isPending}
          onSubmit={(data) =>
            updateMutation.mutate({
              id: String(editing.id),
              data,
            })
          }
        />
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete establishment</AlertDialogTitle>
            <AlertDialogDescription>
              Delete {deleteTarget?.name}? This only succeeds when the school
              has no dependent records. Otherwise the backend will ask you to
              deactivate it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() =>
                deleteTarget && deleteMutation.mutate(String(deleteTarget.id))
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function EstablishmentDialog({
  title,
  open,
  onOpenChange,
  directors,
  initialValue,
  isSubmitting,
  onSubmit,
}: {
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
  directors: SuperAdmin[]
  initialValue: CreateEstablishmentDTO
  isSubmitting: boolean
  onSubmit: (data: CreateEstablishmentDTO) => void
}) {
  const [form, setForm] = useState<CreateEstablishmentDTO>(initialValue)

  const update = (field: keyof CreateEstablishmentDTO, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    onSubmit({
      ...form,
      url: form.url?.trim().toLowerCase() || undefined,
      logo: form.logo?.trim() || undefined,
      description: form.description?.trim() || undefined,
      contactEmail: form.contactEmail?.trim() || undefined,
      contactPhone: form.contactPhone?.trim() || undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="school-name">School name *</Label>
              <Input
                id="school-name"
                required
                value={form.name}
                onChange={(event) => update('name', event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="school-url">URL slug</Label>
              <Input
                id="school-url"
                value={form.url ?? ''}
                onChange={(event) => update('url', event.target.value)}
                placeholder="my-school"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="school-address">Address *</Label>
            <Input
              id="school-address"
              required
              value={form.address}
              onChange={(event) => update('address', event.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Assigned director *</Label>
            <Select
              required
              value={form.superAdminId}
              onValueChange={(value) => update('superAdminId', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a school director" />
              </SelectTrigger>
              <SelectContent>
                {directors.map((director) => (
                  <SelectItem key={director.id} value={director.id}>
                    {director.firstname} {director.lastname} ({director.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="contact-email">Contact email</Label>
              <Input
                id="contact-email"
                type="email"
                value={form.contactEmail ?? ''}
                onChange={(event) => update('contactEmail', event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-phone">Contact phone</Label>
              <Input
                id="contact-phone"
                value={form.contactPhone ?? ''}
                onChange={(event) => update('contactPhone', event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="school-logo">Logo URL</Label>
            <Input
              id="school-logo"
              value={form.logo ?? ''}
              onChange={(event) => update('logo', event.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="school-description">Description</Label>
            <Textarea
              id="school-description"
              value={form.description ?? ''}
              onChange={(event) => update('description', event.target.value)}
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !form.superAdminId}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
